import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import { Plus, FileUp } from '@carboledger/ui/icons';
import { PageHeader } from '@/components/layout';
import { ConfirmDialog } from '@/components/feedback';
import { DiscrepancyModal } from '@/components/common';
import {
  LinkShipmentsForOrder,
  LINK_ORDER_FLOWS,
  aggregateLinkRows,
  buildOrderLinkFields,
} from '@/components/common';
// Update shipment IS the outgoing-shipment form, so it uses that slice's component rather than a copy.
import { OutgoingShipmentForm } from '@/slices/outgoing-shipments/components/OutgoingShipmentForm';
import { ROUTES, MODULE_CERTIFICATE_TYPES } from '@/app/routes';
import { useTranslations, useAssistantFilters } from '@/hooks';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useDeleteOrderMutation } from '../api/ordersApi';
import { buildOrderRowDiscrepancySections } from '../constants/orderDiscrepancy';
import { ORDER_ACTIONS } from '../constants/orderActions';
import { useOrderUpdateShipment } from '../hooks';
import { EMPTY_LIST } from '@/lib/utils';
import styles from './OrdersPage.module.scss';
import { OrdersTable } from '../components/OrdersTable';

// Identity rows the card only shows when every selected order agrees on them.
const ORDER_IDENTITY_KEYS = ['materialName', 'facilityName', 'terminal', 'soldTo', 'shippingTo'];

// Outbounds ▸ Orders.
export const OrdersPage = () => {
  const t = useTranslations();
  const assistantFilters = useAssistantFilters();
  const navigate = useNavigate();
  const { module } = useParams();

  // `certificateType`, not `module`: the list is GraphQL, and the module scopes it as data.
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteOrder] = useDeleteOrderMutation();

  // Ids with a DELETE in flight — gates the double-click RTK Query won't dedupe.
  const deletingIdsRef = useRef(new Set());

  const [discrepancy, setDiscrepancy] = useState(null);
  const discrepancyRow = discrepancy?.row ?? null;
  const handleOpenDiscrepancy = useCallback(
    (row, unitType) => setDiscrepancy({ row, unitType }),
    [],
  );
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancy(null), []);

  const discrepancySections = useMemo(
    () => buildOrderRowDiscrepancySections(t, discrepancyRow, discrepancy?.unitType),
    [t, discrepancyRow, discrepancy],
  );

  // No View action — the Order no. cell is the row's link to its detail page.
  const handleOpenOrder = useCallback(
    (row) => navigate(ROUTES.orderDetail(module, row.id)),
    [navigate, module],
  );

  const [linkOrders, setLinkOrders] = useState(EMPTY_LIST);
  const [linkFlow, setLinkFlow] = useState(LINK_ORDER_FLOWS.orderToShipments);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const tableRef = useRef(null);

  const handleAdd = useCallback(() => {
    navigate(ROUTES.orderCreate(module));
  }, [navigate, module]);

  // The row action passes its own row in, same as handleOpenOrder above.
  const handleFulfill = useCallback(
    (row) => navigate(ROUTES.orderFulfill(module, row.id)),
    [navigate, module],
  );

  // The unit type is whichever one the row is showing in the table.
  const [updateShipmentFor, setUpdateShipmentFor] = useState(null);
  const updateShipmentRow = updateShipmentFor?.row ?? null;
  const handleUpdateShipment = useCallback(
    (row, unitType) => setUpdateShipmentFor({ row, unitType }),
    [],
  );
  const handleCloseUpdateShipment = useCallback(() => setUpdateShipmentFor(null), []);

  const updateShipment = useOrderUpdateShipment({
    module,
    orderId: updateShipmentRow?.id,
    unitType: updateShipmentFor?.unitType,
    open: updateShipmentRow != null,
    onClose: handleCloseUpdateShipment,
  });

  // The update form's read-only text — the row's names behind the order's ids.
  const lockedShipmentDisplayValues = useMemo(
    () => ({
      facility: updateShipmentRow?.facilityName,
      material: updateShipmentRow?.materialName,
      soldTo: updateShipmentRow?.soldTo,
      shippingTo: updateShipmentRow?.shippingTo,
    }),
    [updateShipmentRow],
  );
  // The order's own names, plus a free-text terminal the form must keep.
  const shipmentDisplayValues = useMemo(
    () => ({ ...lockedShipmentDisplayValues, terminal: updateShipment.legacyTerminal }),
    [lockedShipmentDisplayValues, updateShipment.legacyTerminal],
  );

  const handleBulkLinkShipments = useCallback((selected) => {
    setLinkOrders(selected);
    setLinkFlow(LINK_ORDER_FLOWS.ordersToShipment);
    setIsLinkOpen(true);
  }, []);

  const handleCloseLink = useCallback(() => {
    setIsLinkOpen(false);
    setLinkOrders(EMPTY_LIST);
  }, []);

  const handleLinked = useCallback(() => tableRef.current?.clearSelection(), []);

  const bulkActions = useMemo(
    () => [
      {
        id: 'link-shipments',
        label: t('ACTION_LINK_SHIPMENTS'),
        onClick: handleBulkLinkShipments,
      },
    ],
    [t, handleBulkLinkShipments],
  );

  // One order or many, the card is the same ten rows — Figma 4196:111461 / 4196:113122.
  const linkOrder = useMemo(() => aggregateLinkRows(linkOrders, ORDER_IDENTITY_KEYS), [linkOrders]);

  const linkDetailFields = useCallback(
    (unitType, assignedInUnit) => buildOrderLinkFields(t, linkOrder, unitType, assignedInUnit),
    [t, linkOrder],
  );

  const handleEdit = useCallback(
    (row) => {
      navigate(ROUTES.orderEdit(module, row.id));
    },
    [navigate, module],
  );

  const handleDelete = useCallback((row) => setDeleteRow(row), []);
  const handleCancelDelete = useCallback(() => setDeleteRow(null), []);

  const handleConfirmDelete = useCallback(async () => {
    const id = deleteRow?.id;
    if (id == null || deletingIdsRef.current.has(id)) return;
    deletingIdsRef.current.add(id);
    try {
      await deleteOrder({ module, id }).unwrap();
      setDeleteRow(null);
    } catch {
      // notifyApiErrors already surfaced it.
    } finally {
      deletingIdsRef.current.delete(id);
    }
  }, [deleteOrder, module, deleteRow]);

  const addIcon = useMemo(() => <Plus size={16} />, []);
  const importIcon = useMemo(() => <FileUp size={16} />, []);

  const rowActions = useMemo(
    () => [
      {
        id: 'fulfill',
        label: t('ACTION_FULFILL'),
        variant: ButtonVariants.tertiary,
        onClick: handleFulfill,
      },
      {
        id: 'update-shipment',
        label: t('ACTION_UPDATE_SHIPMENT'),
        variant: ButtonVariants.tertiary,
        onClick: handleUpdateShipment,
      },
    ],
    [t, handleFulfill, handleUpdateShipment],
  );

  const rowMenuActions = useMemo(
    () => [
      { id: 'edit', label: t('COMMON_EDIT'), onClick: handleEdit },
      {
        id: 'delete',
        label: t('COMMON_DELETE'),
        disabled: ORDER_ACTIONS.delete.disabled,
        onClick: handleDelete,
      },
    ],
    [t, handleEdit, handleDelete],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('ORDERS_EMPTY_TITLE'),
      description: t('ORDERS_EMPTY_DESCRIPTION'),
      primaryLabel: t('ORDERS_ADD'),
      onPrimaryClick: handleAdd,
      primaryIcon: addIcon,
    }),
    [t, handleAdd, addIcon],
  );

  const noResults = useMemo(
    () => ({
      title: t('ORDERS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const headerActions = useMemo(
    () => (
      <>
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={importIcon}
          // TODO: enable once the shared .xlsx import wizard exists.
          disabled
        >
          {t('ORDERS_IMPORT')}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          onClick={handleAdd}
        >
          {t('ORDERS_ADD')}
        </Button>
      </>
    ),
    [t, handleAdd, importIcon, addIcon],
  );

  return (
    <div className={styles.page}>
      <PageHeader title={t('ORDERS_TITLE')} actions={headerActions} />
      <OrdersTable
        tableRef={tableRef}
        extraFilters={assistantFilters}
        certificateType={MODULE_CERTIFICATE_TYPES[module]}
        emptyState={emptyState}
        noResults={noResults}
        onOpenOrder={handleOpenOrder}
        onOpenDiscrepancy={handleOpenDiscrepancy}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        bulkActions={bulkActions}
      />

      <LinkShipmentsForOrder
        module={module}
        orders={linkOrders}
        flow={linkFlow}
        detailFields={linkDetailFields}
        open={isLinkOpen}
        onClose={handleCloseLink}
        onLinked={handleLinked}
      />

      {updateShipment.ready ? (
        <OutgoingShipmentForm
          key={updateShipmentRow?.id}
          asModal
          open={updateShipmentRow != null}
          title={t('ORDERS_UPDATE_SHIPMENT_TITLE', { orderNo: updateShipmentRow?.orderNo })}
          description={t('ORDERS_UPDATE_SHIPMENT_DESCRIPTION')}
          submitLabel={t('COMMON_SAVE')}
          initialValues={updateShipment.initialValues}
          onSubmit={updateShipment.onSubmit}
          onClose={handleCloseUpdateShipment}
          isSubmitting={updateShipment.isSubmitting}
          options={updateShipment.options}
          lockedFromOrder
          lockedDisplayValues={shipmentDisplayValues}
        />
      ) : null}

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.orderNo}
        message={t('ORDERS_DETAIL_DISCREPANCY_MESSAGE')}
        sections={discrepancySections}
      />

      <ConfirmDialog
        open={deleteRow != null}
        title={t('ORDERS_DELETE_TITLE')}
        bodyTitle={t('ORDERS_DELETE_BODY_TITLE', { orderNo: deleteRow?.orderNo })}
        description={t('ORDERS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
